import { LoadingSpinner, useDynamicCrumb } from '@psyomics/ui-components';
import { Navigate } from 'react-router-dom';

import { ReferralInvite } from '../referral/generated/referral';
import { useReferralInviteQuery } from '../referral/hooks/use-referral-invite-query';

import { SurveyBuilder } from './components/survey-builder';
import { ParsedResult, useInviteSurvey } from './hooks/use-invite-survey';
import { useQuestionnaireStartTracking } from './hooks/use-questionnaire-start-tracking';

interface SurveyProps {
  readonly invitePublicId: string;
  readonly reviewMode?: boolean;
}

const shouldRedirectToHome = (reviewMode: boolean, invite?: ReferralInvite, errorMessage?: string) => {
  if (!invite || errorMessage === 'InviteNotFound') return true;
  if (
    !reviewMode &&
    (invite.referralSession.status === 'expired' || invite.referralSession.referral.status === 'cancelled')
  )
    return true;

  return false;
};

const checkState = (reviewMode: boolean, invite?: ReferralInvite) => {
  const reviewNotAllowed = reviewMode && (invite?.status !== 'completed' || !invite.reviewEnabled);
  if (reviewNotAllowed) throw new Error('cannot review');
};

const parseResult = (result?: ParsedResult) => {
  if (!result?.survey?.definition) throw new Error('no survey definition');
  return {
    definition: result.survey.definition,
    responseId: result.responseId,
    initialisedNow: result.initialisedNow,
  };
};

const Survey = ({ invitePublicId, reviewMode = false }: SurveyProps) => {
  const { result, fetching, errorMessage } = useInviteSurvey(invitePublicId);
  const [invite, inviteFetching] = useReferralInviteQuery(invitePublicId);

  useDynamicCrumb(reviewMode ? invite?.referralSession.referral.service?.description : undefined);
  useQuestionnaireStartTracking(invitePublicId);

  if (fetching || inviteFetching) return <LoadingSpinner />;
  if (shouldRedirectToHome(reviewMode, invite, errorMessage)) return <Navigate to="/" />;
  checkState(reviewMode, invite);

  const { responseId, definition, initialisedNow } = parseResult(result);

  return (
    <SurveyBuilder
      initialisedNow={initialisedNow}
      invitePublicId={invitePublicId}
      reviewMode={reviewMode}
      surveyJson={definition}
      surveyResponseId={responseId}
    />
  );
};

export { Survey };
